import api from './api';
import type {
  ApiResponse,
  Invoice,
  Payment,
  PaymentMethod,
  PaymentPurpose,
  CreatePaymentData,
  UpdatePaymentData,
} from '@/types';

/**
 * Phase 64 D-12 / D-14: payments API client.
 *
 * Routes:
 *   POST   /invoices/{id}/payments  → create (server returns refreshed Invoice)
 *   PATCH  /payments/{id}           → update (server returns refreshed Invoice)
 *   DELETE /payments/{id}           → delete (server returns refreshed Invoice)
 *
 * Phase 64 review WR-02: all three verbs return the refreshed parent
 * InvoiceResource so the FE never has to follow up with a GET /invoices/{id}
 * to render the new derived status / totals after a write.
 *
 * All three are gated by `feature:payments_enabled` server-side — return
 * 404 when the clinic does not have the kill-switch flipped on. The frontend
 * additionally hides the entire payment surface via FF gate at the parent
 * component level (see PaymentHistoryList).
 *
 * Permission gates (Phase 64 D-11):
 *   - payments.create → recordPayment
 *   - payments.edit   → updatePayment
 *   - payments.delete → deletePayment
 * Plus PaymentPolicy's parent-invoice-void guard (no edits/deletes on a
 * voided invoice for any role).
 */

/**
 * Record a new payment against an invoice. Server returns the parent
 * Invoice (refreshed with the new payment, recomputed total_paid +
 * balance_due, and auto-derived status (draft → partial → paid)).
 */
export const recordPayment = async (
  invoiceId: string,
  data: CreatePaymentData,
): Promise<Invoice> => {
  const response = await api.post<ApiResponse<Invoice>>(
    `/invoices/${invoiceId}/payments`,
    data,
  );
  return response.data.data;
};

/**
 * Update an existing payment. Server-side UpdatePaymentRequest enforces the
 * edit-aware cumulative-overpayment check (existing payments excluding the
 * row being edited + new amount ≤ invoice.total_amount). WR-02: returns the
 * refreshed parent Invoice (not just the edited Payment row).
 */
export const updatePayment = async (
  paymentId: string,
  data: UpdatePaymentData,
): Promise<Invoice> => {
  const response = await api.patch<ApiResponse<Invoice>>(
    `/payments/${paymentId}`,
    data,
  );
  return response.data.data;
};

/**
 * Delete a payment. Server-side PaymentPolicy::delete enforces
 * `payments.delete` permission + parent-invoice-not-void guard.
 * The PaymentObserver re-derives the parent invoice status (paid → partial
 * → sent → draft) after the row is removed. WR-02: returns the refreshed
 * parent Invoice (200 + body, not 204 No Content).
 */
export const deletePayment = async (paymentId: string): Promise<Invoice> => {
  const response = await api.delete<ApiResponse<Invoice>>(`/payments/${paymentId}`);
  return response.data.data;
};

/**
 * Phase 67 D-02 — Record a refund against a payment.
 *
 * Backend: POST /api/v1/payments/{id}/refund with body { reason }.
 * Returns the updated Payment with refunded_at + refund_reason populated.
 *
 * Server-side: PaymentObserver::saved fires → Invoice::recomputeStatus()
 * re-derives the parent invoice status (paid→partial or partial→sent)
 * because Invoice::totalPaid() filters whereNull('refunded_at').
 *
 * Caller (RefundPaymentDialog) should follow up with the parent
 * onPaymentChange() callback so the invoice detail page refetches and
 * the row strikes through with the "Refunded {date}" annotation.
 *
 * Idempotency: server returns 422 if `refunded_at` is already non-null.
 */
export const refundPayment = async (
  paymentId: string,
  data: string | { reason: string; amount?: string; refund_date: string; method: string; money_account_id: string; idempotency_key: string },
): Promise<Payment> => {
  const response = await api.post<ApiResponse<Payment>>(
    `/payments/${paymentId}/refund`,
    typeof data === "string" ? { reason: data } : data,
  );
  return response.data.data;
};

/**
 * Dentour 260923 — Mark as paid / Mark as unpaid (payments.confirm). The card
 * or bank-transfer money has shown on the POS settlement / bank statement, so
 * it starts (or stops) counting toward the invoice and every report.
 */
export const confirmPayment = async (paymentId: string): Promise<Payment> => {
  const response = await api.post<ApiResponse<Payment>>(`/payments/${paymentId}/confirm`);
  return response.data.data;
};

export const unconfirmPayment = async (paymentId: string): Promise<Payment> => {
  const response = await api.delete<ApiResponse<Payment>>(`/payments/${paymentId}/confirm`);
  return response.data.data;
};
export const settlePayment = async (id: string, data: { settlement_date: string; money_account_id?: string; idempotency_key: string }): Promise<Payment> =>
  (await api.post<ApiResponse<Payment>>(`/payments/${id}/settle`, data)).data.data;

export const correctPayment = async (id: string, data: { money_account_id: string; reason: string; idempotency_key: string; corrected_amount?: string; corrected_method?: PaymentMethod }): Promise<Payment> =>
  (await api.post<ApiResponse<Payment>>(`/payments/${id}/correct`, data)).data.data;

// ── All-payments index (cross-invoice list page) ────────────────────────────
// GET /payments — paginated, plus a `summary` block (total collected,
// this-month, by-method). Gated by feature:payments_enabled + invoices.view
// (see PaymentPolicy::viewAny — the capture's payments.view key is seeded nowhere).

export interface PaymentListRow {
  money_tracking_required?: boolean;
  currency?: string | null;
  original_payment_id?: string | null;
  allocation_gross_amount?: number;
  refunded_amount?: number;
  cancelled_at?: string | null;
  settlement_status?: "legacy" | "pending" | "received" | "cancelled";
  id: string;
  invoice_id: string | null;
  lead_id: string | null;
  is_deposit: boolean;
  amount: number;
  method: string;
  /** ArtDent 260924 — the clinic account a POS / transfer landed in. */
  bank_account?: { id: string; name: string } | null;
  /** ArtDent 260929 — the Cash & banks account, on a clinic that tracks money. */
  money_account_id?: string | null;
  money_account?: { id: string; name: string; kind: string; bank_name: string | null } | null;
  payment_date: string | null;
  receipt_number: string | null;
  notes: string | null;
  refunded_at: string | null;
  recorded_by?: { id: string; full_name: string };
  invoice?: { id: string; invoice_number: string; status: string } | null;
  lead?: { id: string; full_name: string } | null;
  /**
   * ArtDent 260916 — what kind of movement this row is. DERIVED server-side
   * (no invoice = deposit, refunded = refund), and absent entirely on clinics
   * without the money view, which is what keeps their list unchanged.
   */
  kind?: MovementKind
  /** ArtDent 260917 — what the money is for, and whether it is accounted for. */
  purpose?: PaymentPurpose
  is_travel?: boolean
  travel_settled?: number | null
  travel_remaining?: number | null;
  /** The payment method, echoed for the Type column. */
  detail?: string | null;
  /**
   * ArtDent 260916 — how much of this DEPOSIT an adjustment has given back,
   * with the reason typed at the time. Present only on a consumed deposit.
   * Display-only: the row's `amount` and the collected total are unchanged,
   * because the clinic did receive the money. See PaymentController.
   */
  adjusted_amount?: number | null;
  adjusted_reason?: string | null;
  date?: string | null;
  confirmed_at?: string | null;
  /** Dentour 260923 — see Payment.requires_confirmation. */
  requires_confirmation?: boolean;
  confirmed_by?: { id: number; full_name: string } | null;
  proof_count?: number;
}

/**
 * ArtDent 260916 — what kind of cash movement a row is. Adjustments are
 * deliberately absent: they change what the patient owes rather than moving
 * money, and they live on the patient's ledger.
 */
export type MovementKind = 'payment' | 'deposit' | 'refund';

export interface PaymentsSummary {
  /** Collected over the FILTERED set — moves with the date range on screen. */
  total_collected: number;
  /**
   * Standing period totals. Unlike `total_collected` these ignore the list's
   * date range (ArtDent 260916): the page opens on today, and a range-filtered
   * "today" would just restate `total_collected`.
   */
  today: number;
  this_month: number;
  /**
   * ArtDent 260916 — money recorded but not yet received. Date-blind like the
   * period tiles above: a transfer from three weeks ago is exactly the one
   * worth chasing, and hiding it behind the list's range is how it gets
   * forgotten.
   */
  awaiting_confirmation: number;
  awaiting_confirmation_count: number;
  /**
   * ArtDent 260917 — travel money the clinic is holding for patients and has
   * not spent yet. Date-blind like the tiles above, and for the same reason: a
   * flight deposit nobody has spent is exactly the one worth finding.
   */
  travel_open?: number;
  travel_open_count?: number;
  by_method: { method: string; total: number; count: number }[];
}

export interface PaymentListParams {
  search?: string;
  method?: string;
  /** ArtDent 260924 — one clinic bank account. */
  bank_account_id?: string;
  /** ArtDent 260929 — one Cash & banks account. */
  money_account_id?: string;
  /** ArtDent 260930 — several methods at once, comma-separated ("card,bank-transfer"). */
  methods?: string;
  /** ArtDent 260930 — 'missing': rows with no document filed against them. */
  proof?: 'missing';
  /** Narrow to rows still owed a document ('awaiting') or settled ones. */
  confirmation?: 'awaiting' | 'confirmed';
  /** ArtDent 260916 — one kind of movement. */
  type?: MovementKind;
  /** ArtDent 260917 — one purpose, or 'travel' for all the non-dental ones. */
  purpose?: PaymentPurpose | 'travel';
  /** Travel money still holding a remainder, or fully accounted for. */
  resolution?: 'open' | 'settled';
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}

export interface PaymentListResponse {
  data: PaymentListRow[];
  summary: PaymentsSummary;
  meta: {
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
  };
}

export const listAllPayments = async (
  params: PaymentListParams = {},
): Promise<PaymentListResponse> => {
  const response = await api.get('/payments', { params });
  const body = response.data as PaymentListResponse;
  return { data: body.data, summary: body.summary, meta: body.meta };
};

/**
 * Issue (or re-serve) the printable receipt for a payment, and open it.
 *
 * POST because the FIRST call mints a receipt number and files the PDF into the
 * patient's Media & Files; later calls re-serve the same document. Fetched as a
 * blob rather than linked, because the endpoint takes a Bearer token — an
 * `<a href>` would arrive unauthenticated and 401.
 *
 * Returns an object URL the caller must revoke; leaving them attached would
 * hold every receipt printed this session in memory.
 */
export const printPaymentReceipt = async (paymentId: string): Promise<string> => {
  const response = await api.post(`/payments/${paymentId}/receipt`, null, {
    responseType: "blob",
  });

  return URL.createObjectURL(response.data as Blob);
};

// ── Confirmations board (ArtDent 260930) ────────────────────────────────────
// GET /payments/confirmation-stats — what the accountant who only confirms
// money needs at the start of the day. "Confirmed" counts by confirmation date.

export interface ConfirmationStats {
  awaiting: { count: number; total: number; oldest_days: number | null };
  confirmed: Record<'today' | 'week' | 'month', { count: number; total: number }>;
  card_missing_receipt: { count: number; total: number };
  terminals: Array<{ id: string; name: string; currency: string; balance: string }>;
  recent_confirmations: Array<{
    id: string;
    amount: number;
    currency: string | null;
    confirmed_at: string | null;
    lead: { id: string; full_name: string } | null;
    invoice: { id: string; invoice_number: string } | null;
    account: string | null;
    confirmed_by: string | null;
  }>;
}

export const getConfirmationStats = async (): Promise<ConfirmationStats> =>
  (await api.get<ApiResponse<ConfirmationStats>>('/payments/confirmation-stats')).data.data;
